import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import NewReleasesRoundedIcon from '@mui/icons-material/NewReleasesRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import axiosInstance from "../../api/axiosInstance";
import "./notifications.scss";
import moment from "moment";

function Notifications({ onUnreadChange, handleClick }) {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const loadNotifications = useCallback(async (showLoading = false) => {
        try {
            if (showLoading) setLoading(true);

            const response = await axiosInstance.get("notifications?hours=24");

            const data = response.data.notifications;
            setNotifications(data);

            onUnreadChange?.(
                data.filter(
                    (notification) => Number(notification.isRead) === 0
                ).length
            );
        } catch (error) {
            console.error(
                "Fehler beim Laden der Benachrichtigungen:",
                error
            );
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [onUnreadChange]);

    useEffect(() => {
        loadNotifications(true);

        const interval = setInterval(() => {
            loadNotifications();
        }, 10000);

        return () => clearInterval(interval);
    }, [loadNotifications]);

    const handleNotificationClick = async (notification) => {
        try {
            if (Number(notification.isRead) === 0) {
                await axiosInstance.patch(
                    `notifications/${notification.id}/read`
                );

                setNotifications((previous) => {
                    const updated = previous.map((item) =>
                        item.id === notification.id
                            ? { ...item, isRead: 1 }
                            : item
                    );

                    onUnreadChange?.(
                        updated.filter(
                            (item) => Number(item.isRead) === 0
                        ).length
                    );

                    return updated;
                });
            }

            if (notification.link) {
                navigate(notification.link);
            }
        } catch (error) {
            console.error(
                "Fehler beim Aktualisieren der Benachrichtigung:",
                error
            );
        }
    };

    const handleNavigation = (path) => {
        handleClick?.(); // Popup schließen
        navigate(path);
    };

    function getNotificationIcon(type) {
        switch (type) {
            case 'info':
                return <NotificationsActiveRoundedIcon />;

            case 'success':
                return <CheckRoundedIcon style={{ background: "#007bff" }} />;

            case 'warning':
                return <WarningRoundedIcon style={{ background: "#ff7700" }} />;

            case 'error':
                return <NewReleasesRoundedIcon style={{ background: "#cd071e" }} />;

            default:
                return <ForumRoundedIcon />;
        }
    }

    if (loading) {
        return (
            <div className="notificationList">
                <div className="loading" />
            </div>
        );
    }

    return (
        <div className="notificationList">
            <h5 className="heading">Benachrichtigungen</h5>
            <hr />
            {notifications.length === 0 ? (
                <div className="notificationEmpty">
                    Keine Benachrichtigungen
                </div>
            ) : (
                <div className="scroll">
                    <div className="list">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={notification.isRead ? "notificationItem" : "notificationItem unread"}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                {getNotificationIcon(notification.type)}
                                <div className="content">
                                    <div className="top">
                                        <h5 className="title">{notification.title}</h5>

                                        {Number(notification.isRead) === 0 && (
                                            <div className="dot" />
                                        )}
                                    </div>
                                    <p className="message">{notification.message}</p>
                                    <span className="date">
                                        {moment(notification.createdAt).fromNow()}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <hr />
            <button onClick={() => handleNavigation("/logging")} className="allNotifications">Alle Benachrichtigungen öffnen</button>
        </div>
    );
}

export default Notifications;